import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import "./AuthPrompt.css";
import { useNavigate, useLocation } from 'react-router-dom';
import { logEvent } from "./logger";
import { FcGoogle } from "react-icons/fc"; // Import Google icon


export default function AuthPrompt({ onAuth }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState("gate");
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "Weak",
    color: "#ff4d4f"
  });
  const [isVerified, setIsVerified] = useState(true);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Track recent signup attempts per email
  const recentSignups = {};

  // Check auth state on mount
 const from = location.state?.from?.pathname || "/";

useEffect(() => {
  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("name")
        .eq("id", user.id)
        .single();

      if (profileData?.name) {
        onAuth(profileData.name);
        navigate(from);

        setIsVerified(!!user.email_confirmed_at);
      }
    }
  };

  checkAuth();
}, [navigate, onAuth, from]);

  const checkPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    const strengthLevels = [
      { label: "Weak", color: "#ff4d4f" },
      { label: "Fair", color: "#faad14" },
      { label: "Good", color: "#52c41a" },
      { label: "Strong", color: "#1890ff" }
    ];
    
    // Set current strength
    const strength = strengthLevels[Math.min(score, 3)];
    setPasswordStrength(strength);

    // Return boolean if strong enough (Fair or higher)
    return score >= 1;
  };

  const handleGoogleSignIn = async () => {
  setIsGoogleLoading(true);
  try {
    logEvent("info", "Google sign-in attempt");

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`, // Home page
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });

    if (error) throw error;
    logEvent("success", "Google sign-in initiated");

  } catch (error) {
    console.error("Google sign-in error:", error);
    alert(`Google sign-in failed: ${error.message}`);
  } finally {
    setIsGoogleLoading(false);
  }
};




  const handleSignup = async () => {
    setIsLoading(true);
    try {
      // Check password strength first
      if (!checkPasswordStrength(password)) {
        alert("Password too weak. Must be at least Fair: 8+ chars, 1 uppercase, 1 number or symbol.");
        setIsLoading(false);
        return;
      }

      const now = Date.now();
      const lastAttempt = recentSignups[email] || 0;

      // 1-minute cooldown per email
      if (now - lastAttempt < 60_000) {
        alert("Please wait a minute before trying again.");
        logEvent("warn", "Signup blocked by rate limit", { email });
        setIsLoading(false);
        return;
      }

      // Record this attempt
      recentSignups[email] = now;

      logEvent("info", "Signup attempt", { email });

      const { data, error } = await supabase.auth.signUp({
        email,
        password, 
      });

      if (error) {
        logEvent("error", "Signup failed", { error: error.message });
        throw error;
      }

      // Save extra profile info
      await supabase.from("profiles").upsert({
        id: data.user.id,
        name: name,
        email: email,
      });

      logEvent("success", "Signup successful", { userId: data.user.id });

      alert("Signup successful! Please check your email to verify your account.");
      setView("landing");

    } catch (error) {
      logEvent("error", "Unexpected error during signup", { error: error.message });
      console.error("Signup error:", error);
      alert(`Signup failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const {data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      const { data: profileData } = await supabase
        .from("profiles")
        .select("name")
        .eq("id", data.user.id)
        .single();

      if (!profileData?.name) {
        throw new Error("Welcome to CLT Student Hub! Hope you enjoy your stay! --Shivang Patel");
      }

      onAuth(profileData.name);
      navigate('/'); // redirect to home page


      // Show verification banner if email not verified
      if (!data.user.email_confirmed_at) {
        setIsVerified(false);
        logEvent("warn", "User email not verified", { email });
      } else {
        setIsVerified(true);
      }

    } catch (error) {
      alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuest = () => {
    onAuth("Guest");
    navigate('/');
  };

  const handlePasswordReset = async () => {
    if (!email) {
      alert("Please enter your email first");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`
      });
      
      if (error) throw error;
      
      setResetSent(true);
      setTimeout(() => setResetSent(false), 5000);
    } catch (error) {
      console.error('Password reset error:', error);
      alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Display verification banner if needed
  const renderVerificationBanner = () => {
    if (!isVerified) {
      return (
        <div className="email-verification-banner">
          ⚠ Please verify your email. Check your inbox or spam folder.
        </div>
      );
    }
    return null;
  };

  // Authentication Gate
  if (view === "gate") {
    return (
      <div className="auth-gate-container">
        {renderVerificationBanner()}
        <div className="auth-gate-modal">
          <h2 className="auth-title">CLT Student Hub</h2>

          
          {/* Google Sign-In Button */}
          <button 
            className="google-signin-btn"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
          >
            <FcGoogle className="google-icon" />
            {isGoogleLoading ? "Connecting..." : "Continue with Google"}
          </button>
          
 
          <div className="divider">
            <span>or</span>
          </div>
          
          <div className="auth-gate-options">
            <button className="auth-option primary" onClick={() => setView("login")}>Log In</button>
            <button className="auth-option secondary" onClick={handleGuest}>Browse as Guest</button>
          </div>
          <div className="auth-gate-footer">
            <p>Don't have an account?</p>
            <button className="text-button" onClick={() => setView("signup")}>Sign Up</button>
          </div>
        </div>
      </div>
    );
  }

  // Landing page
  if (view === "landing") {
    return (
      <div className="auth-container">
        {renderVerificationBanner()}
        <h2 className="auth-title">CLT Student Hub</h2>
        
        {/* Google Sign-In Button */}
        <button 
          className="google-signin-btn"
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading}
        >
          <FcGoogle className="google-icon" />
          {isGoogleLoading ? "Connecting..." : "Continue with Google"}
        
</button>
        
        <div className="divider">
          <span>OR</span>
        </div>
        
        <button className="auth-option" onClick={() => setView("signup")}>Sign Up</button>
        <button className="auth-option" onClick={() => setView("login")}>Log In</button>
        <button className="auth-option" onClick={handleGuest}>Browse as Guest</button>
        <button className="secondary-option" onClick={() => setView("reset")}>Forgot Password?</button>
      </div>
    );
  }

  if (view === "signup") {
    return (
      <div className="auth-container">
        {renderVerificationBanner()}
        <h2>Sign Up</h2>
        
        {/* Google Sign-In Button */}
        <button 
          className="google-signin-btn"
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading}
        >
          <FcGoogle className="google-icon" />
          {isGoogleLoading ? "Connecting..." : "Sign up with Google"}
        </button>
        
        <div className="divider">
          <span>or</span>
        </div>
        
        <div className="auth-form">
          <input type="text" placeholder="First Name" value={name} onChange={(e) => setName(e.target.value)} />
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input type="password" placeholder="Password" value={password} onChange={(e) => {
            setPassword(e.target.value);
            checkPasswordStrength(e.target.value);
          }} />
          <div className="password-strength">
            <div className="strength-bar" style={{ width: `${(passwordStrength.score + 1) * 25}%`, backgroundColor: passwordStrength.color }}></div>
            <span style={{ color: passwordStrength.color }}>{passwordStrength.label}</span>
          </div>
          <button className="auth-option" onClick={handleSignup} disabled={isLoading}>
            {isLoading ? "Signing Up..." : "Sign Up"}
          </button>
          <button className="secondary-option" onClick={() => setView("gate")}>Back</button>
        </div>
      </div>
    );
  }

  if (view === "login") {
    return (
      <div className="auth-container">
        {renderVerificationBanner()}
        <h2>Log In</h2>
        
        {/* Google Sign-In Button */}
        <button 
          className="google-signin-btn"
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading}
        >
          <FcGoogle className="google-icon" />
          {isGoogleLoading ? "Connecting..." : "Log in with Google"}
        </button>
        
        <div className="divider">
          <span> or  </span>
        </div>
        
        <div className="auth-form">
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button className="auth-option" onClick={handleLogin} disabled={isLoading}>
            {isLoading ? "Logging In..." : "Log In"}
          </button>
          <button className="secondary-option" onClick={() => setView("gate")}>Back</button>
          <button className="secondary-option" onClick={() => setView("reset")}>Forgot Password?</button>
        </div>
      </div>
    );
  }

  if (view === "reset") {
    return (
      <div className="auth-container">
        {renderVerificationBanner()}
        <h2>Reset Password</h2>
        <div className="auth-form">
          <input type="email" placeholder="Your Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button className="auth-option" onClick={handlePasswordReset} disabled={isLoading}>
            {resetSent ? "Email Sent!" : isLoading ? "Sending..." : "Send Reset Link"}
          </button>
          <button className="secondary-option" onClick={() => setView("login")}>Back to Login</button>
          {resetSent && <p className="reset-instructions">Check your email for a login link. This link will expire in 1 hour.</p>}
        </div>
      </div>
    );
  }

  return null;
}